"use client";

import * as React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";
import { ChevronDown, ChevronRight } from "lucide-react";

/**
 * Sidebar Primitives
 *
 * Composable primitives for building custom sidebars:
 * - SidebarRoot: Outer container with collapse state
 * - SidebarHeader: Header area with logo and title
 * - SidebarContent: Scrollable content area
 * - SidebarFooter: Footer area
 * - SidebarSection: Collapsible section with title
 * - SidebarItem: Navigation item with optional icon/badge
 * - SidebarToggle: Collapse/expand button
 */

// Context for sharing collapse state
interface SidebarContextValue {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
}

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

export function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarRoot");
  }
  return context;
}

// =============================================================================
// SidebarRoot
// =============================================================================
export interface SidebarRootProps extends React.HTMLAttributes<HTMLElement> {
  /** Position of the sidebar */
  position?: "left" | "right";
  /** Width when expanded (px or CSS value) */
  width?: number | string;
  /** Controlled collapsed state */
  collapsed?: boolean;
  /** Default collapsed state */
  defaultCollapsed?: boolean;
  /** Callback when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void;
}

export function SidebarRoot({
  className,
  position = "left",
  width = 280,
  collapsed: controlledCollapsed,
  defaultCollapsed = false,
  onCollapsedChange,
  children,
  style,
  ...props
}: SidebarRootProps) {
  const [uncontrolledCollapsed, setUncontrolledCollapsed] =
    React.useState(defaultCollapsed);

  const isControlled = controlledCollapsed !== undefined;
  const collapsed = isControlled ? controlledCollapsed : uncontrolledCollapsed;

  const setCollapsed = React.useCallback(
    (value: boolean) => {
      if (!isControlled) {
        setUncontrolledCollapsed(value);
      }
      onCollapsedChange?.(value);
    },
    [isControlled, onCollapsedChange]
  );

  const parsedWidth = typeof width === "number" ? `${width}px` : width;

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      <aside
        data-slot="sidebar-root"
        data-collapsed={collapsed}
        className={cn(
          styleDrivers.sidebar.root,
          position === "left" ? "border-r" : "border-l",
          className
        )}
        style={{
          width: collapsed ? undefined : parsedWidth,
          ...style,
        }}
        {...props}
      >
        {children}
      </aside>
    </SidebarContext.Provider>
  );
}

// =============================================================================
// SidebarHeader
// =============================================================================
export interface SidebarHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Logo element */
  logo?: React.ReactNode;
  /** Title when expanded */
  headerTitle?: React.ReactNode;
  /** Show collapse toggle */
  showToggle?: boolean;
}

export function SidebarHeader({
  className,
  logo,
  headerTitle,
  showToggle = true,
  children,
  ...props
}: SidebarHeaderProps) {
  const { collapsed, setCollapsed } = useSidebar();

  return (
    <div
      data-slot="sidebar-header"
      className={cn(
        styleDrivers.sidebar.headerRow,
        collapsed ? "flex-col justify-center" : "justify-between",
        className
      )}
      {...props}
    >
      <div className="flex items-center space-x-2 p-2">
        {logo && <div>{logo}</div>}
        {!collapsed && headerTitle && (
          <div className={cn(styleDrivers.sidebar.headerTitle)}>{headerTitle}</div>
        )}
      </div>
      {showToggle && (
        <button
          type="button"
          className={cn(styleDrivers.sidebar.toggleButton)}
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
      )}
      {children}
    </div>
  );
}

// =============================================================================
// SidebarContent
// =============================================================================
export type SidebarContentProps = React.HTMLAttributes<HTMLDivElement>;

export function SidebarContent({
  className,
  children,
  ...props
}: SidebarContentProps) {
  return (
    <nav
      data-slot="sidebar-content"
      className={cn(styleDrivers.sidebar.nav, className)}
      {...props}
    >
      {children}
    </nav>
  );
}

// =============================================================================
// SidebarFooter
// =============================================================================
export type SidebarFooterProps = React.HTMLAttributes<HTMLDivElement>;

export function SidebarFooter({
  className,
  children,
  ...props
}: SidebarFooterProps) {
  const { collapsed } = useSidebar();

  return (
    <div
      data-slot="sidebar-footer"
      className={cn(
        styleDrivers.sidebar.footerWrapper,
        collapsed && "opacity-0 pointer-events-none",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// =============================================================================
// SidebarSection
// =============================================================================
export interface SidebarSectionProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Section heading */
  sectionTitle?: React.ReactNode;
  /** Allow collapse */
  collapsible?: boolean;
  /** Default collapsed state */
  defaultCollapsed?: boolean;
  /** Icon for section */
  icon?: React.ReactNode;
}

export function SidebarSection({
  className,
  sectionTitle,
  collapsible = false,
  defaultCollapsed = false,
  icon,
  children,
  ...props
}: SidebarSectionProps) {
  const { collapsed: sidebarCollapsed } = useSidebar();
  const [sectionCollapsed, setSectionCollapsed] =
    React.useState(defaultCollapsed);

  const toggleSection = () => {
    if (collapsible) {
      setSectionCollapsed(!sectionCollapsed);
    }
  };

  const showContent = !sectionCollapsed || sidebarCollapsed;

  return (
    <div
      data-slot="sidebar-section"
      className={cn(styleDrivers.sidebar.section, className)}
      {...props}
    >
      {sectionTitle && !sidebarCollapsed && (
        <div
          className={cn(
            styleDrivers.sidebar.sectionTitleRow,
            collapsible && styleDrivers.sidebar.sectionTitleInteractive
          )}
          onClick={toggleSection}
          role={collapsible ? "button" : undefined}
          aria-expanded={collapsible ? !sectionCollapsed : undefined}
        >
          <div className="flex items-center gap-2">
            {icon && <div className="text-muted-foreground">{icon}</div>}
            <div className={cn(styleDrivers.sidebar.sectionTitleText)}>
              {sectionTitle}
            </div>
          </div>
          {collapsible && (
            <ChevronDown
              className={cn(
                styleDrivers.sidebar.expandIconWrapper,
                sectionCollapsed && "-rotate-90"
              )}
            />
          )}
        </div>
      )}
      {showContent && (
        <div className={cn(styleDrivers.sidebar.itemsList)}>
          {children}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SidebarItem
// =============================================================================
export interface SidebarItemProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  /** Navigation URL */
  href?: string;
  /** Item icon */
  icon?: React.ReactNode;
  /** Badge/count indicator */
  badge?: React.ReactNode;
  /** Whether the item is currently active */
  active?: boolean;
  /** Disable the item */
  disabled?: boolean;
  /** Click handler (for non-link items) */
  onClick?: () => void;
}

export function SidebarItem({
  className,
  href,
  icon,
  badge,
  active,
  disabled,
  onClick,
  children,
  ...props
}: SidebarItemProps) {
  const { collapsed } = useSidebar();

  const content = (
    <>
      {icon && (
        <div className={cn(styleDrivers.sidebar.itemIcon)}>{icon}</div>
      )}
      {!collapsed && (
        <>
          <div className={cn(styleDrivers.sidebar.itemLabel)}>{children}</div>
          {badge && (
            <div className={cn(styleDrivers.sidebar.itemBadgeWrapper)}>{badge}</div>
          )}
        </>
      )}
    </>
  );

  const itemClasses = cn(
    styleDrivers.sidebar.itemRow,
    active && "bg-accent ring-primary/20 ring-[1.5px]",
    disabled && styleDrivers.sidebar.itemRowDisabled,
    className
  );

  if (href && !disabled) {
    return (
      <Link to={href} className={itemClasses} {...props}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={itemClasses}
      onClick={onClick}
      disabled={disabled}
      {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {content}
    </button>
  );
}

// =============================================================================
// SidebarToggle (standalone toggle button)
// =============================================================================
export interface SidebarToggleProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Custom expand icon */
  expandIcon?: React.ReactNode;
  /** Custom collapse icon */
  collapseIcon?: React.ReactNode;
}

export function SidebarToggle({
  className,
  expandIcon,
  collapseIcon,
  ...props
}: SidebarToggleProps) {
  const { collapsed, setCollapsed } = useSidebar();

  return (
    <button
      type="button"
      className={cn(styleDrivers.sidebar.toggleButton, className)}
      onClick={() => setCollapsed(!collapsed)}
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      {...props}
    >
      {collapsed
        ? (expandIcon ?? <ChevronRight className="h-4 w-4" />)
        : (collapseIcon ?? <ChevronDown className="h-4 w-4" />)}
    </button>
  );
}

// Export all primitives
export const SidebarPrimitives = {
  Root: SidebarRoot,
  Header: SidebarHeader,
  Content: SidebarContent,
  Footer: SidebarFooter,
  Section: SidebarSection,
  Item: SidebarItem,
  Toggle: SidebarToggle,
};
